import React, { useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";

import PageTitle from "../../../layouts/PageTitle";
import { appApi, useGetEquipmentListQuery } from "../../../../store/appApi";
import { toggleEquipmentStatusApi } from "../../../../services/equipementService";

const getAlertBadge = (item) => {
  if (item.serviceMode === "Hour-Based" && item.alertIntervalMonths) {
    const totalHours = (item.serviceHistory || [])
      .filter((s) => s.serviceType === "Hour-Based")
      .reduce((sum, s) => sum + (s.metricValue || 0), 0);
    if (totalHours >= item.alertIntervalMonths) {
      return <span className="badge badge-danger light">HOURS EXCEEDED</span>;
    }
    if (totalHours >= item.alertIntervalMonths * 0.9) {
      return <span className="badge badge-warning light">NEAR LIMIT</span>;
    }
  }

  if (item.serviceMode === "Fuel Consumption" && item.alertFuelThreshold) {
    const consumed = (item.serviceHistory || [])
      .filter((s) => s.serviceType === "Fuel Consumption")
      .reduce((sum, s) => sum + (s.metricValue || 0), 0);
    if (consumed >= item.alertFuelThreshold) {
      return <span className="badge badge-danger light">THRESHOLD REACHED</span>;
    }
    if (consumed >= item.alertFuelThreshold * 0.9) {
      return <span className="badge badge-warning light">NEAR THRESHOLD</span>;
    }
  }

  return null;
};

const STATUS_BADGE = {
  Active: "success",
  Inactive: "secondary",
  "Under Service": "warning",
  Breakdown: "danger",
  Relocating: "info",
};

const ListEquipement = () => {
  const dispatch = useDispatch();
  const { data, isLoading, isError, error } = useGetEquipmentListQuery();
  const items = data?.items || [];

  const [togglingId, setTogglingId] = useState(null);

  const handleToggle = async (item) => {
    const newStatus = item.eqpStatus === "Active" ? "Inactive" : "Active";
    const label = newStatus === "Active" ? "activate" : "deactivate";

    const confirm = await Swal.fire({
      title: `${label.charAt(0).toUpperCase() + label.slice(1)} equipment?`,
      text: `"${item.eqpName}" will be marked as ${newStatus}.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Yes, ${label}`,
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;

    setTogglingId(item._id);
    try {
      await toggleEquipmentStatusApi(item._id, newStatus);
      dispatch(appApi.util.invalidateTags(["Equipment"]));
    } catch (err) {
      Swal.fire(
        "Error",
        err?.response?.data?.message || "Failed to update status.",
        "error",
      );
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <>
      <PageTitle activeMenu="Equipment List" motherMenu="Daily Progress" />

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h4 className="card-title mb-0">Equipment Log</h4>
              <Link
                to="/progress/equipement/add"
                className="btn btn-primary btn-sm"
              >
                + Add Equipment
              </Link>
            </div>
            <div className="card-body">
              {isLoading && (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status" />
                </div>
              )}

              {isError && !isLoading && (
                <div className="alert alert-danger">
                  {error?.data?.message || "Failed to load equipment list."}
                </div>
              )}

              {!isLoading && !isError && items.length === 0 && (
                <div className="text-center text-muted py-4">
                  No equipment logs found. Start by adding one.
                </div>
              )}

              {!isLoading && items.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-bordered table-hover verticle-middle">
                    <thead className="thead-dark">
                      <tr>
                        {/* <th>#</th> */}
                        <th>Date</th>
                        <th>Project</th>
                        <th>Equipment Name</th>
                        <th>Type</th>
                        <th>Service Mode</th>
                        <th>Number</th>
                        {/* <th>Make / Model</th>
                        <th>Year</th> */}
                        <th>Condition</th>
                        <th>Status</th>
                        <th>Alert</th>
                        <th>Added By</th>
                        <th>Active</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, index) => {
                        const isActive = item.eqpStatus === "Active";
                        const isToggling = togglingId === item._id;
                        return (
                          <tr key={item._id}>
                            {/* <td>{index + 1}</td> */}
                            <td>
                              <span className="d-block">
                                {moment(item.created).format("DD MMM YYYY")}
                              </span>
                              <small className="text-muted">
                                {moment(item.created).format("hh:mm A")}
                              </small>
                            </td>
                            <td>{item.projectName}</td>
                            <td>
                              <Link
                                to={`/progress/equipement/${item._id}/view`}
                                className="fw-bold text-primary"
                              >
                                {item.eqpName}
                              </Link>
                            </td>
                            <td>
                              <span className="badge badge-primary light">
                                {item.eqpType}
                              </span>
                            </td>
                            <td>
                              {item.serviceMode ? (
                                <span className={`badge light ${item.serviceMode === "Hour-Based" ? "badge-info" : "badge-warning"}`}>
                                  <i className={`la ${item.serviceMode === "Hour-Based" ? "la-clock-o" : "la-tint"} me-1`} />
                                  {item.serviceMode}
                                </span>
                              ) : "—"}
                            </td>
                            <td>{item.eqpNumber || "—"}</td>
                            {/* <td>
                              {[item.eqpMake, item.eqpModel].filter(Boolean).join(" / ") || "—"}
                            </td>
                            <td>{item.eqpYear || "—"}</td> */}
                            <td>
                              {item.eqpCondition &&
                              item.eqpCondition !== "—" ? (
                                <span
                                  className={`badge light badge-${
                                    item.eqpCondition === "New"
                                      ? "success"
                                      : item.eqpCondition === "Good"
                                        ? "info"
                                        : item.eqpCondition === "Fair"
                                          ? "warning"
                                          : "danger"
                                  }`}
                                >
                                  {item.eqpCondition}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td>
                              <span
                                className={`badge light badge-${
                                  STATUS_BADGE[item.eqpStatus] || "secondary"
                                }`}
                              >
                                {item.eqpStatus || "Active"}
                              </span>
                            </td>
                            <td>{getAlertBadge(item) || <span className="text-muted">—</span>}</td>
                            <td>{item.addedByName}</td>
                            <td>
                              {isToggling ? (
                                <span className="spinner-border spinner-border-sm text-primary" />
                              ) : (
                                <div className="d-flex align-items-center gap-2">
                                  <div className="custom-control custom-switch">
                                    <input
                                      type="checkbox"
                                      className="custom-control-input"
                                      id={`toggle-${item._id}`}
                                      checked={isActive}
                                      onChange={() => handleToggle(item)}
                                    />
                                    <label
                                      className="custom-control-label"
                                      htmlFor={`toggle-${item._id}`}
                                      style={{ cursor: "pointer" }}
                                    />
                                  </div>
                                  <small
                                    className={
                                      isActive ? "text-success" : "text-muted"
                                    }
                                  >
                                    {isActive ? "On" : "Off"}
                                  </small>
                                </div>
                              )}
                            </td>
                            <td>
                              <Link
                                to={`/progress/equipement/${item._id}/view`}
                                className="btn btn-xs btn-outline-primary d-flex align-items-center gap-1"
                                title="View detail"
                              >
                                <i className="fa fa-eye" /> Detail
                              </Link>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ListEquipement;
